import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowUpRight,
  BellRing,
  Calendar as CalIcon,
  Camera,
  CheckCircle2,
  ChevronRight,
  CircleDot,
  Clock3,
  Download,
  Edit3,
  Eye,
  EyeOff,
  FileText,
  FilePlus2,
  Hammer,
  Inbox,
  Info,
  MessageSquare,
  Paperclip,
  Plus,
  Rocket,
  Send,
  Share2,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Upload,
  Users,
  Zap,
} from "lucide-react";
import { LaunchHubSidebar } from "../components/launchhub/LaunchHubSidebar";
import { PremiumInput } from "../components/brand/PremiumInput";
import { Avatar, AvatarStack } from "../components/Avatar";
import {
  property,
  seller,
  leadNegotiator,
  officeTeam,
  launchStages,
  readinessChecklist,
  atAGlance,
  nextAction,
  teamChatMessages,
  brochure,
  viewings,
  calendarWeek,
  feedback as feedbackData,
  sellerDocuments,
  marketInsights,
  activityTimeline,
} from "../data/launchHubData";

// ───────────────────────────────────────────────
// Hook: live countdown (days · hours · mins · secs)
// ───────────────────────────────────────────────
const useCountdown = (target) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const total = Math.max(0, target - now);
  const d = Math.floor(total / 86400000);
  const h = Math.floor((total / 3600000) % 24);
  const m = Math.floor((total / 60000) % 60);
  const s = Math.floor((total / 1000) % 60);
  return { d, h, m, s, total };
};

// ───────────────────────────────────────────────
// Hook: animated number (0 → target on mount)
// ───────────────────────────────────────────────
const useAnimatedNumber = (target, duration = 1400) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const step = (t) => {
      const elapsed = t - start;
      const p = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return n;
};

// ───────────────────────────────────────────────
// Hook: scroll spy
// ───────────────────────────────────────────────
const useScrollSpy = (ids, offset = 160) => {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY + offset;
      let current = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= y) current = id;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [ids, offset]);
  return active;
};

// ───────────────────────────────────────────────
// Section wrapper
// ───────────────────────────────────────────────
const Section = ({ id, eyebrow, title, action, children, testid }) => (
  <section id={id} data-testid={testid} className="cf-fade-up scroll-mt-24">
    <div className="flex items-end justify-between gap-4 mb-5">
      <div>
        <p className="cf-eyebrow cf-eyebrow-emerald mb-2">{eyebrow}</p>
        <h2 className="cf-h-section">{title}</h2>
      </div>
      {action}
    </div>
    {children}
  </section>
);

// ───────────────────────────────────────────────
// Hero — Property card + workflow stages
// ───────────────────────────────────────────────
const PropertyHero = () => {
  // Target: 22 Feb 2026 09:00 (≈142h from instruction-active reference)
  const launchTarget = useMemo(() => {
    const target = new Date();
    target.setDate(target.getDate() + 6);
    target.setHours(9, 0, 0, 0);
    return target.getTime();
  }, []);
  const { d, h, m, s } = useCountdown(launchTarget);
  const team = [leadNegotiator, ...officeTeam];

  const stagePct =
    (launchStages.findIndex((x) => x.state === "active") /
      Math.max(launchStages.length - 1, 1)) *
    100;

  return (
    <div
      className="relative overflow-hidden rounded-[28px] border border-slate-200/70 bg-white cf-shadow-lg"
      data-testid="property-hero-card"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_440px]">
        {/* Image side */}
        <div className="relative h-[300px] lg:h-auto min-h-[340px]">
          <img
            src={property.image}
            alt={property.address}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-[#0A1128]/85 via-[#0A1128]/30 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A1128]/55" />
          {/* Status badge */}
          <div className="absolute top-5 left-5 flex items-center gap-2">
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold backdrop-blur-md"
              style={{
                background: "rgba(16, 185, 129, 0.18)",
                color: "#A7F3D0",
                border: "1px solid rgba(16,185,129,0.40)",
              }}
              data-testid="hero-status-badge"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 cf-pulse" />
              {property.status}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold backdrop-blur-md bg-white/12 border border-white/25 text-white">
              {property.region}
            </span>
          </div>

          {/* Address + price */}
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <p className="text-[10.5px] uppercase tracking-[0.24em] text-indigo-200/95 font-semibold mb-2">
              Active instruction · {property.id.toUpperCase()}
            </p>
            <h1 className="font-display text-white text-[28px] lg:text-[32px] font-bold leading-tight tracking-tight">
              {property.shortAddress}
            </h1>
            <p className="text-slate-300 text-[12.5px] mt-1.5">{property.address}</p>
            <div className="mt-4 flex items-center gap-5 text-white/95">
              <span className="font-display text-[22px] font-bold cf-num">{property.guidePrice}</span>
              <span className="h-3 w-px bg-white/30" />
              <span className="text-[12.5px]">{property.bedrooms} bed</span>
              <span className="text-[12.5px]">{property.bathrooms} bath</span>
              <span className="text-[12.5px] cf-num">
                {property.sqft.toLocaleString()} sq ft
              </span>
            </div>
          </div>
        </div>

        {/* Right meta panel */}
        <div className="relative p-7 flex flex-col gap-5 bg-gradient-to-b from-white via-white to-indigo-50/30">
          {/* Team */}
          <div>
            <p className="cf-eyebrow mb-3">Account team</p>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <Avatar person={leadNegotiator} size={40} online />
                <div className="min-w-0">
                  <p className="text-[13.5px] font-bold text-slate-900 truncate">
                    {leadNegotiator.name}
                  </p>
                  <p className="text-[11px] text-slate-500 truncate">
                    {leadNegotiator.role} · {leadNegotiator.branch}
                  </p>
                </div>
              </div>
              <AvatarStack people={officeTeam} size={28} max={3} />
            </div>
            <div className="mt-3 flex items-center gap-2.5 p-2.5 rounded-xl bg-indigo-50/70 border border-indigo-100">
              <Avatar person={seller} size={26} />
              <p className="text-[11.5px] text-slate-700">
                <span className="font-semibold">Seller</span> · {seller.name}
              </p>
            </div>
          </div>

          <div className="cf-hairline" />

          {/* Countdown */}
          <div data-testid="launch-countdown">
            <div className="flex items-center justify-between mb-2">
              <p className="cf-eyebrow">Launch goes live</p>
              <span className="text-[10.5px] text-slate-500">{property.launchTarget}</span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {[
                { v: d, l: "Days" },
                { v: h, l: "Hrs" },
                { v: m, l: "Min" },
                { v: s, l: "Sec" },
              ].map((u, i) => (
                <div
                  key={u.l}
                  className="rounded-xl border border-slate-200 bg-white p-2.5 text-center cf-shadow-0"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  <p className="font-display text-slate-900 text-[22px] font-bold cf-num leading-none">
                    {String(u.v).padStart(2, "0")}
                  </p>
                  <p className="text-[9.5px] uppercase tracking-[0.18em] text-slate-500 mt-1.5 font-semibold">
                    {u.l}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Workflow stages */}
          <div>
            <div className="flex items-center justify-between mb-2.5">
              <p className="cf-eyebrow">Launch workflow</p>
              <span className="text-[10.5px] text-indigo-700 font-semibold cf-num">
                Stage 2 / {launchStages.length}
              </span>
            </div>
            <div className="relative">
              <div className="absolute left-0 right-0 top-[10px] h-[2px] rounded-full bg-slate-200/80" />
              <div
                className="absolute left-0 top-[10px] h-[2px] rounded-full bg-gradient-to-r from-indigo-400 via-indigo-500 to-indigo-600 transition-all duration-700"
                style={{ width: `${Math.max(stagePct, 8)}%` }}
              />
              <div className="relative grid grid-cols-5 gap-1">
                {launchStages.map((stg) => {
                  const isComplete = stg.state === "complete";
                  const isActive = stg.state === "active";
                  return (
                    <div key={stg.id} className="flex flex-col items-center text-center">
                      <span
                        className={`relative w-[22px] h-[22px] rounded-full flex items-center justify-center ${
                          isComplete
                            ? "bg-indigo-600 border-2 border-indigo-600"
                            : isActive
                            ? "bg-white border-2 border-indigo-500 cf-glow"
                            : "bg-white border-2 border-slate-300"
                        }`}
                      >
                        {isComplete && <CheckCircle2 size={11} className="text-white" />}
                        {isActive && <span className="w-2 h-2 rounded-full bg-indigo-500 cf-pulse" />}
                      </span>
                      <span
                        className={`mt-2 text-[10px] font-semibold leading-tight ${
                          isComplete || isActive ? "text-slate-900" : "text-slate-500"
                        }`}
                      >
                        {stg.label}
                      </span>
                      <span className="text-[9px] text-slate-400 mt-0.5">{stg.date}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ───────────────────────────────────────────────
// Right hero panel (Readiness + Glance + Next action)
// ───────────────────────────────────────────────
const ReadinessRing = ({ value }) => {
  const animated = useAnimatedNumber(value, 1600);
  const size = 168;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (animated / 100) * c;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <defs>
          <linearGradient id="ring-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#A5B4FC" />
            <stop offset="50%" stopColor="#6366F1" />
            <stop offset="100%" stopColor="#4338CA" />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#F1F5F9" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="url(#ring-grad)"
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
          style={{ filter: "drop-shadow(0 0 12px rgba(99, 102, 241,0.45))" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-slate-900 font-bold cf-num text-[40px] leading-none">
          {animated}%
        </span>
        <span className="text-[9.5px] uppercase tracking-[0.22em] text-slate-500 mt-1.5 font-semibold">
          Ready to launch
        </span>
      </div>
    </div>
  );
};

const StateDot = ({ state }) => {
  const colors = {
    complete: "bg-emerald-500",
    "in-progress": "bg-indigo-500 cf-pulse",
    warning: "bg-amber-500",
    upcoming: "bg-slate-300",
  };
  return <span className={`inline-block w-2 h-2 rounded-full ${colors[state] || "bg-slate-300"}`} />;
};

const RightHeroPanel = () => {
  return (
    <div className="space-y-5" data-testid="right-hero-panel">
      {/* Readiness card */}
      <div className="cf-card-elevated p-6" data-testid="readiness-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="cf-eyebrow cf-eyebrow-emerald mb-1.5">Seller readiness</p>
            <h3 className="cf-h-section text-[16px]">Launch Readiness Score</h3>
          </div>
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-indigo-50 text-indigo-700 text-[10px] font-semibold uppercase tracking-[0.16em] border border-indigo-100">
            <Sparkles size={10} /> Live
          </span>
        </div>
        <div className="flex items-center justify-center mb-4">
          <ReadinessRing value={property.readiness} />
        </div>
        <div className="space-y-2">
          {readinessChecklist.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-50 transition-colors"
              data-testid={`readiness-${item.id}`}
            >
              <div className="flex items-center gap-2.5">
                <StateDot state={item.state} />
                <span className="text-[12.5px] text-slate-800 font-medium">{item.label}</span>
              </div>
              {item.note && (
                <span className="text-[10.5px] text-slate-500 font-medium">{item.note}</span>
              )}
              {!item.note && item.state === "complete" && (
                <CheckCircle2 size={14} className="text-emerald-500" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* At a glance */}
      <div className="cf-card-elevated p-6" data-testid="at-a-glance-card">
        <p className="cf-eyebrow cf-eyebrow-emerald mb-1.5">Pulse</p>
        <h3 className="cf-h-section text-[16px] mb-4">At a Glance</h3>
        {[
          { l: "Brochure", v: atAGlance.brochureStatus, icon: FileText },
          { l: "Missing docs", v: atAGlance.missingDocuments, icon: Inbox },
          { l: "Next viewing", v: atAGlance.nextViewing, icon: Eye },
          { l: "Latest feedback", v: atAGlance.latestFeedback, icon: MessageSquare },
          { l: "Seller responsiveness", v: atAGlance.sellerResponsiveness, icon: Zap },
        ].map((row) => {
          const Icon = row.icon;
          const toneColor =
            row.v.tone === "ok"
              ? "text-emerald-700"
              : row.v.tone === "warn"
              ? "text-amber-700"
              : "text-slate-700";
          return (
            <div
              key={row.l}
              className="flex items-center justify-between gap-3 py-2.5 border-b border-slate-100 last:border-0"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <Icon size={13} className="text-slate-400 shrink-0" />
                <span className="text-[12px] text-slate-500 font-medium">{row.l}</span>
              </div>
              <span className={`text-[12px] font-semibold text-right truncate ${toneColor}`}>
                {row.v.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Next action */}
      <div
        className="relative overflow-hidden rounded-[22px] p-6 cf-shadow-lg border border-indigo-200"
        style={{
          background:
            "linear-gradient(140deg, #1E1B4B 0%, #3730A3 60%, #4338CA 100%)",
        }}
        data-testid="next-action-card"
      >
        <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-indigo-400/30 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-12 w-44 h-44 rounded-full bg-indigo-400/20 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-indigo-200 font-semibold mb-2">
            <BellRing size={11} /> Agent next action
          </div>
          <h3 className="font-display text-white text-[18px] font-bold leading-tight tracking-tight">
            {nextAction.title}
          </h3>
          <p className="text-indigo-100/85 text-[12.5px] mt-2 leading-relaxed">
            {nextAction.description}
          </p>
          <div className="mt-4 flex items-center gap-2 text-[11px] text-indigo-100/75">
            <Clock3 size={12} /> Due {nextAction.due}
          </div>
          <button
            className="mt-4 w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-white text-indigo-900 font-semibold text-[12.5px] hover:bg-indigo-50 transition-all"
            data-testid="send-reminder-btn"
          >
            <Send size={13} /> {nextAction.ctaLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ───────────────────────────────────────────────
// Section 1 — Team Chat
// ───────────────────────────────────────────────
const TeamChat = () => {
  const [draft, setDraft] = useState("");
  const peopleById = useMemo(() => {
    const map = {};
    [leadNegotiator, ...officeTeam, seller].forEach((p) => (map[p.id] = p));
    return map;
  }, []);

  const quickActions = [
    { id: "brochure", label: "Send Brochure", icon: FileText },
    { id: "request", label: "Request Document", icon: FilePlus2 },
    { id: "viewing", label: "Suggest Viewing", icon: Eye },
    { id: "feedback", label: "Share Feedback", icon: MessageSquare },
  ];

  return (
    <div className="cf-card-elevated overflow-hidden" data-testid="team-chat-card">
      <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <AvatarStack people={[leadNegotiator, ...officeTeam, seller]} size={28} max={4} />
          <div>
            <p className="text-[13px] font-bold text-slate-900">Aigburth Office · Eleanor Whitfield</p>
            <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 cf-pulse" /> 3 online · all
              messages encrypted
            </p>
          </div>
        </div>
        <button
          className="btn btn-ghost"
          data-testid="chat-history-btn"
          aria-label="Chat history"
        >
          <Share2 size={14} /> Share
        </button>
      </div>

      <div
        className="px-6 py-5 space-y-4 max-h-[520px] overflow-y-auto cf-scroll bg-gradient-to-b from-white to-slate-50/40"
        data-testid="team-chat-messages"
      >
        {teamChatMessages.map((msg) => {
          const sender = peopleById[msg.from];
          const isSeller = msg.from === "seller-1";
          return (
            <div
              key={msg.id}
              className={`flex gap-3 ${isSeller ? "flex-row-reverse" : ""}`}
              data-testid={`chat-msg-${msg.id}`}
            >
              <Avatar person={sender} size={32} />
              <div className={`flex-1 min-w-0 ${isSeller ? "items-end flex flex-col" : ""}`}>
                <div
                  className={`flex items-baseline gap-2 mb-1 ${
                    isSeller ? "flex-row-reverse" : ""
                  }`}
                >
                  <span className="text-[12px] font-semibold text-slate-900">{sender.name}</span>
                  <span className="text-[10.5px] text-slate-400">{msg.at}</span>
                </div>
                <div
                  className={`inline-block max-w-[640px] px-4 py-2.5 rounded-2xl text-[13px] leading-relaxed ${
                    isSeller
                      ? "bg-gradient-to-br from-indigo-600 to-indigo-700 text-white rounded-tr-sm"
                      : msg.type === "intro"
                      ? "bg-indigo-50 text-slate-800 border border-indigo-100 rounded-tl-sm"
                      : "bg-white text-slate-800 border border-slate-200 rounded-tl-sm cf-shadow-0"
                  }`}
                >
                  {msg.body}
                  {msg.file && (
                    <div className="mt-3 flex items-center gap-3 p-3 rounded-xl bg-white border border-slate-200">
                      <div className="w-10 h-10 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                        <FileText size={16} className="text-indigo-700" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[12.5px] font-semibold text-slate-900 truncate">
                          {msg.file.name}
                        </p>
                        <p className="text-[10.5px] text-slate-500 mt-0.5 cf-num">
                          {msg.file.pages} pages · {msg.file.size}
                        </p>
                      </div>
                      <button className="btn btn-secondary !h-7 !px-2 !text-[11px]" aria-label="Download">
                        <Download size={11} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Quick actions */}
      <div className="px-6 py-3 border-t border-slate-100 bg-white flex flex-wrap gap-2">
        {quickActions.map((a) => {
          const Icon = a.icon;
          return (
            <button
              key={a.id}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-semibold text-slate-700 bg-slate-50 hover:bg-indigo-50 hover:text-indigo-700 border border-slate-200 hover:border-indigo-200 transition-all"
              data-testid={`chat-quick-${a.id}`}
            >
              <Icon size={12} /> {a.label}
            </button>
          );
        })}
      </div>

      {/* Input */}
      <div className="px-6 py-4 border-t border-slate-100 bg-white flex items-center gap-3">
        <button
          className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-200 hover:bg-indigo-50 hover:border-indigo-200 text-slate-600 hover:text-indigo-700 flex items-center justify-center transition-all"
          data-testid="chat-attach-btn"
          aria-label="Attach file"
        >
          <Paperclip size={15} />
        </button>
        <div className="flex-1">
          <PremiumInput
            value={draft}
            onChange={setDraft}
            placeholder="Message Eleanor and the team…"
            testid="chat-input"
          />
        </div>
        <button
          className="btn btn-primary"
          data-testid="chat-send-btn"
          onClick={() => setDraft("")}
        >
          <Send size={13} /> Send
        </button>
      </div>
    </div>
  );
};

// ───────────────────────────────────────────────
// Section 2 — Brochure Review
// ───────────────────────────────────────────────
const BrochureReview = () => {
  return (
    <div className="cf-card-elevated p-6 grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-6" data-testid="brochure-review-card">
      {/* PDF preview */}
      <div className="relative">
        <div
          className="aspect-[3/4] rounded-2xl overflow-hidden border border-slate-200 cf-shadow-md relative bg-slate-100"
        >
          <img
            src={brochure.thumbnail}
            alt="Brochure cover preview"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/80 via-transparent to-transparent" />
          <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur text-[10px] font-bold uppercase tracking-[0.18em] text-slate-700 border border-white">
            PDF · {brochure.version}
          </div>
          <div className="absolute bottom-3 left-3 right-3 text-white">
            <p className="font-display text-[14px] font-bold leading-tight tracking-tight">
              12 Mersey Reach
            </p>
            <p className="text-[10.5px] opacity-90 mt-0.5">Cabinet Grotesk · Spring 2026</p>
          </div>
        </div>
        <div className="flex items-center justify-between mt-3 px-1">
          <span className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
            Page 1 / {brochure.pages}
          </span>
          <div className="flex items-center gap-1">
            <button className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center transition-colors" aria-label="Previous page">
              <ChevronRight size={13} className="rotate-180" />
            </button>
            <button className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center transition-colors" aria-label="Next page">
              <ChevronRight size={13} />
            </button>
          </div>
        </div>
      </div>

      {/* Meta + actions */}
      <div className="flex flex-col">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="cf-eyebrow cf-eyebrow-emerald mb-1.5">Brochure</p>
            <h3 className="font-display text-[20px] font-bold text-slate-900 tracking-tight leading-tight">
              {brochure.filename}
            </h3>
            <p className="text-[12px] text-slate-500 mt-1.5">
              Uploaded {brochure.uploadedAt} · {brochure.pages} pages · {brochure.size}
            </p>
          </div>
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-800 border border-amber-200"
            data-testid="brochure-seller-status"
          >
            <CircleDot size={11} /> {brochure.sellerStatus}
          </span>
        </div>

        {/* Seller note */}
        <div className="mt-5 rounded-2xl bg-indigo-50/60 border border-indigo-100 p-4 relative" data-testid="brochure-seller-note">
          <div className="flex items-center gap-2 mb-2">
            <Avatar person={seller} size={22} />
            <p className="text-[11.5px] font-semibold text-indigo-900">
              {seller.name} · edits requested
            </p>
            <span className="text-[10.5px] text-indigo-600 ml-auto">Wed · 16:11</span>
          </div>
          <p className="text-[13px] text-slate-800 leading-relaxed italic">
            “{brochure.sellerNote}”
          </p>
        </div>

        {/* Buttons */}
        <div className="mt-auto pt-5 flex flex-wrap gap-2">
          <button className="btn btn-secondary" data-testid="brochure-download-btn">
            <Download size={13} /> Download PDF
          </button>
          <button className="btn btn-primary" data-testid="brochure-approve-btn">
            <CheckCircle2 size={13} /> Approve Brochure
          </button>
          <button className="btn btn-ghost" data-testid="brochure-request-edits-btn">
            <Edit3 size={13} /> Request Edits
          </button>
        </div>
      </div>
    </div>
  );
};

// ───────────────────────────────────────────────
// Section 3 — Viewing Requests
// ───────────────────────────────────────────────
const ViewingRequests = () => {
  const tone = (s) =>
    s === "Confirmed"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : s === "Pending"
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : "bg-indigo-50 text-indigo-700 border-indigo-200";

  return (
    <div className="cf-card-elevated overflow-hidden" data-testid="viewings-card">
      <div className="grid grid-cols-[1.4fr_1fr_0.9fr_1.2fr_1fr_auto] px-6 py-3 text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold border-b border-slate-100 bg-slate-50/60">
        <span>Buyer</span>
        <span>Date</span>
        <span>Time</span>
        <span>Agent</span>
        <span>Status</span>
        <span></span>
      </div>
      {viewings.map((v) => (
        <div
          key={v.id}
          className="grid grid-cols-[1.4fr_1fr_0.9fr_1.2fr_1fr_auto] items-center px-6 py-3.5 border-b border-slate-100 last:border-0 cf-row"
          data-testid={`viewing-row-${v.id}`}
        >
          <div className="min-w-0">
            <p className="text-[13px] font-bold text-slate-900 truncate">{v.buyer}</p>
            <p className="text-[11px] text-slate-500 truncate mt-0.5">{v.notes}</p>
          </div>
          <span className="text-[12.5px] text-slate-700 font-medium">{v.date}</span>
          <span className="cf-num text-[12.5px] text-slate-900 font-semibold">{v.time}</span>
          <span className="text-[12.5px] text-slate-700">{v.agent}</span>
          <span>
            <span
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-semibold border ${tone(
                v.status
              )}`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
              {v.status}
            </span>
          </span>
          <button
            className="btn btn-ghost !h-8 !px-2 !text-[11px]"
            data-testid={`viewing-add-cal-${v.id}`}
            aria-label="Add to shared calendar"
          >
            <CalIcon size={12} /> Add
          </button>
        </div>
      ))}
      <div className="px-6 py-4 bg-slate-50/40 flex items-center justify-between">
        <p className="text-[11.5px] text-slate-500">
          5 viewings · {viewings.filter((v) => v.status === "Confirmed").length} confirmed for launch
          weekend
        </p>
        <button className="btn btn-dark" data-testid="add-shared-calendar-btn">
          <CalIcon size={13} /> Add to Shared Calendar
        </button>
      </div>
    </div>
  );
};

// ───────────────────────────────────────────────
// Section 4 — Shared Calendar (week view)
// ───────────────────────────────────────────────
const SharedCalendar = () => {
  const kindStyles = {
    viewing: { bg: "rgba(99, 102, 241,0.16)", color: "#5B21B6", border: "rgba(99, 102, 241,0.32)", label: "Viewing" },
    photo: { bg: "rgba(244,114,182,0.16)", color: "#9D174D", border: "rgba(244,114,182,0.30)", label: "Photo" },
    epc: { bg: "rgba(16,185,129,0.16)", color: "#065F46", border: "rgba(16,185,129,0.30)", label: "EPC" },
    contractor: { bg: "rgba(245,158,11,0.16)", color: "#92400E", border: "rgba(245,158,11,0.30)", label: "Contractor" },
    compliance: { bg: "rgba(59,130,246,0.16)", color: "#1E3A8A", border: "rgba(59,130,246,0.30)", label: "Compliance" },
    milestone: { bg: "rgba(99,102,241,0.16)", color: "#3730A3", border: "rgba(99,102,241,0.30)", label: "Milestone" },
    launch: { bg: "linear-gradient(135deg,#4F6BFF,#4F46E5)", color: "#ffffff", border: "rgba(99, 102, 241,0.4)", label: "Launch" },
  };

  const startHour = calendarWeek.hours[0];
  const endHour = calendarWeek.hours[calendarWeek.hours.length - 1] + 1;
  const hourCount = endHour - startHour;
  const rowH = 52;
  const totalH = hourCount * rowH;

  return (
    <div className="cf-card-elevated p-6 overflow-hidden" data-testid="calendar-card">
      <div className="flex items-center justify-between gap-3 mb-5">
        <div>
          <p className="text-[13px] text-slate-500 mt-0.5">{calendarWeek.weekLabel}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {Object.entries(kindStyles).filter(([k]) => k !== "launch").map(([k, st]) => (
            <span
              key={k}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-semibold border"
              style={{ background: st.bg, color: st.color, borderColor: st.border }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: st.color }} /> {st.label}
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-[60px_repeat(7,minmax(0,1fr))] border border-slate-200 rounded-2xl overflow-hidden bg-white">
        {/* Header */}
        <div className="bg-slate-50/60 border-b border-slate-200" />
        {calendarWeek.days.map((d, i) => {
          const isLaunch = i === 5;
          return (
            <div
              key={d}
              className={`px-3 py-3 text-center border-b border-slate-200 ${isLaunch ? "relative" : ""}`}
              style={
                isLaunch
                  ? {
                      background:
                        "linear-gradient(180deg, rgba(99, 102, 241,0.18) 0%, rgba(99, 102, 241,0.04) 100%)",
                    }
                  : { background: "rgba(248,250,252,0.60)" }
              }
            >
              {isLaunch && (
                <span className="absolute top-1.5 right-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-[0.16em] bg-indigo-600 text-white cf-pulse">
                  <Rocket size={9} /> LIVE
                </span>
              )}
              <p className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                {d.split(" ")[0]}
              </p>
              <p className={`text-[16px] cf-num font-bold mt-0.5 ${isLaunch ? "text-indigo-700" : "text-slate-900"}`}>
                {d.split(" ")[1]}
              </p>
            </div>
          );
        })}

        {/* Grid body */}
        <div className="border-r border-slate-200" style={{ height: totalH }}>
          {calendarWeek.hours.map((h) => (
            <div
              key={h}
              className="text-[10.5px] cf-num text-slate-400 text-right pr-2 border-b border-slate-100"
              style={{ height: rowH, paddingTop: 4 }}
            >
              {h}:00
            </div>
          ))}
        </div>

        {Array.from({ length: 7 }).map((_, dayIdx) => (
          <div
            key={`day-col-${calendarWeek.days[dayIdx]}`}
            className="relative border-r border-slate-100 last:border-r-0"
            style={{ height: totalH, background: dayIdx === 5 ? "rgba(99, 102, 241,0.04)" : "transparent" }}
          >
            {calendarWeek.hours.map((hour) => (
              <div
                key={`day-${dayIdx}-row-${hour}`}
                className="border-b border-slate-100"
                style={{ height: rowH }}
              />
            ))}
            {calendarWeek.events
              .filter((e) => e.day === dayIdx)
              .map((e, idx) => {
                const top = (e.start - startHour) * rowH + 2;
                const height = (e.end - e.start) * rowH - 4;
                const st = kindStyles[e.kind];
                const isLaunch = e.kind === "launch";
                return (
                  <div
                    key={`evt-${dayIdx}-${e.kind}-${e.start}-${e.title}`}
                    className="absolute left-1.5 right-1.5 rounded-lg px-2.5 py-1.5 overflow-hidden cf-shadow-0"
                    style={{
                      top,
                      height,
                      background: st.bg,
                      color: st.color,
                      border: `1px solid ${st.border}`,
                      boxShadow: isLaunch ? "0 8px 22px -6px rgba(99, 102, 241,0.55)" : undefined,
                    }}
                    data-testid={`cal-event-${dayIdx}-${idx}`}
                  >
                    <p className={`text-[10.5px] font-bold leading-tight truncate ${isLaunch ? "" : ""}`}>
                      {e.title}
                    </p>
                    <p className="text-[9.5px] mt-0.5 opacity-85 truncate">{e.agent}</p>
                  </div>
                );
              })}
          </div>
        ))}
      </div>
    </div>
  );
};

// ───────────────────────────────────────────────
// Section 5 — Latest Feedback
// ───────────────────────────────────────────────
const FeedbackSection = () => {
  const [filter, setFilter] = useState("all");
  const filtered = feedbackData.filter((f) => filter === "all" || f.visibility === filter);

  const potentialTone = (p) =>
    p === "High"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : p === "Medium"
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : "bg-slate-50 text-slate-700 border-slate-200";

  return (
    <div className="space-y-4" data-testid="feedback-card">
      {/* Toggle */}
      <div className="flex items-center justify-between">
        <div
          className="inline-flex items-center gap-1 p-1 rounded-xl bg-slate-100 border border-slate-200"
          data-testid="feedback-filter"
        >
          {[
            { id: "all", label: "All" },
            { id: "seller", label: "Visible to seller" },
            { id: "internal", label: "Internal only" },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 rounded-lg text-[11.5px] font-semibold transition-all ${
                filter === f.id
                  ? "bg-white text-indigo-700 cf-shadow-0"
                  : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid={`feedback-filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <p className="text-[11.5px] text-slate-500">{filtered.length} entries</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {filtered.map((f) => (
          <div
            key={f.id}
            className="cf-card-elevated p-5 flex flex-col"
            data-testid={`feedback-${f.id}`}
          >
            <div className="flex items-start justify-between gap-2 mb-3">
              <div>
                <p className="text-[13px] font-bold text-slate-900">{f.buyer}</p>
                <p className="text-[10.5px] text-slate-500 mt-0.5">{f.date} viewing</p>
              </div>
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-[0.14em] border ${potentialTone(
                  f.potential
                )}`}
              >
                {f.potential} potential
              </span>
            </div>
            <p className="text-[13px] text-slate-700 leading-relaxed italic">“{f.quote}”</p>
            <div className="mt-3 mb-4">
              <div className="flex items-center justify-between text-[10.5px] mb-1.5">
                <span className="text-slate-500 uppercase tracking-[0.16em] font-semibold">Interest</span>
                <span className="cf-num font-bold text-slate-900">{f.interest}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-indigo-400 to-indigo-600"
                  style={{ width: `${f.interest}%` }}
                />
              </div>
            </div>
            <div className="mt-auto pt-3 border-t border-slate-100 flex items-center justify-between">
              <span
                className={`inline-flex items-center gap-1.5 text-[10.5px] font-semibold ${
                  f.visibility === "seller" ? "text-indigo-700" : "text-slate-500"
                }`}
              >
                {f.visibility === "seller" ? <Eye size={11} /> : <EyeOff size={11} />}
                {f.visibility === "seller" ? "Visible to seller" : "Internal only"}
              </span>
              <button className="text-[11.5px] cf-link inline-flex items-center gap-1">
                Open <ArrowUpRight size={11} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ───────────────────────────────────────────────
// Section 6 — Documents Hub
// ───────────────────────────────────────────────
const DocumentsHub = () => {
  const statusStyle = (s) => {
    if (s === "Approved") return "bg-emerald-50 text-emerald-700 border-emerald-200";
    if (s === "Uploaded") return "bg-indigo-50 text-indigo-700 border-indigo-200";
    if (s === "Needs Review") return "bg-amber-50 text-amber-700 border-amber-200";
    return "bg-rose-50 text-rose-700 border-rose-200"; // Missing
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="documents-hub">
      {sellerDocuments.map((d) => {
        const missing = d.status === "Missing";
        return (
          <div
            key={d.id}
            className={`cf-card-elevated p-5 flex flex-col ${missing ? "border-dashed" : ""}`}
            data-testid={`doc-${d.id}`}
          >
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center">
                {missing ? (
                  <Upload size={16} className="text-rose-500" />
                ) : (
                  <FileText size={16} className="text-indigo-700" />
                )}
              </div>
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-[0.14em] border ${statusStyle(
                  d.status
                )}`}
              >
                {d.status}
              </span>
            </div>
            <p className="text-[13.5px] font-bold text-slate-900 leading-tight">{d.name}</p>
            <p className="text-[10.5px] text-slate-500 mt-1 uppercase tracking-[0.16em] font-semibold">
              {d.category}
            </p>
            {d.updated && (
              <p className="text-[11px] text-slate-500 mt-2">Updated {d.updated}</p>
            )}
            <div className="mt-auto pt-4">
              {missing ? (
                <button
                  className="w-full btn btn-primary !h-9 !text-[11.5px]"
                  data-testid={`doc-upload-${d.id}`}
                >
                  <Upload size={12} /> Upload Document
                </button>
              ) : (
                <button
                  className="w-full btn btn-secondary !h-9 !text-[11.5px]"
                  data-testid={`doc-view-${d.id}`}
                >
                  <Eye size={12} /> View
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ───────────────────────────────────────────────
// Section 7 — Market Readiness Insights (AI)
// ───────────────────────────────────────────────
const MarketReadinessInsights = () => {
  return (
    <div
      className="relative overflow-hidden rounded-[24px] cf-shadow-lg"
      style={{
        background:
          "linear-gradient(140deg, #0F172A 0%, #1E1B4B 50%, #312E81 100%)",
      }}
      data-testid="market-insights-card"
    >
      <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-indigo-500/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-indigo-500/15 blur-3xl pointer-events-none" />

      <div className="relative p-7">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-indigo-200 font-semibold mb-2">
              <Sparkles size={11} /> Chain intelligence
            </div>
            <h3 className="font-display text-white text-[22px] font-bold tracking-tight">
              Market readiness for Aigburth · L17
            </h3>
            <p className="text-indigo-100/85 text-[12.5px] mt-1.5 max-w-xl">
              Real-time signals from 142 comparable instructions in the last 12 months.
            </p>
          </div>
          <button
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 backdrop-blur text-white text-[12px] font-semibold border border-white/15 transition-colors"
            data-testid="view-full-insights-btn"
          >
            View Full Insights <ArrowUpRight size={13} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {marketInsights.map((i) => (
            <div
              key={i.id}
              className="rounded-2xl p-5 backdrop-blur border border-white/10"
              style={{ background: "rgba(255,255,255,0.06)" }}
              data-testid={`insight-${i.id}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-400/15 border border-emerald-400/30 text-emerald-200 text-[10px] font-bold uppercase tracking-[0.14em]">
                  <TrendingUp size={10} /> {i.impact}
                </span>
              </div>
              <p className="font-display text-white text-[14.5px] font-bold leading-tight tracking-tight">
                {i.headline}
              </p>
              <p className="text-indigo-100/75 text-[12px] mt-2 leading-relaxed">{i.detail}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ───────────────────────────────────────────────
// Section 8 — Activity Timeline
// ───────────────────────────────────────────────
const ActivityTimelineSection = () => {
  const iconFor = (k) => {
    const map = {
      reminder: BellRing,
      chat: MessageSquare,
      approval: ShieldCheck,
      upload: Upload,
      compliance: ShieldCheck,
      viewing: Eye,
    };
    return map[k] || Info;
  };
  const tintFor = (k) => {
    const map = {
      reminder: "bg-amber-50 text-amber-700 border-amber-200",
      chat: "bg-indigo-50 text-indigo-700 border-indigo-200",
      approval: "bg-blue-50 text-blue-700 border-blue-200",
      upload: "bg-emerald-50 text-emerald-700 border-emerald-200",
      compliance: "bg-blue-50 text-blue-700 border-blue-200",
      viewing: "bg-rose-50 text-rose-700 border-rose-200",
    };
    return map[k] || "bg-slate-50 text-slate-700 border-slate-200";
  };

  return (
    <div className="cf-card-elevated p-7" data-testid="activity-timeline-card">
      <div className="relative">
        <div className="absolute left-[19px] top-2 bottom-2 w-px bg-gradient-to-b from-indigo-200 via-slate-200 to-transparent" />
        <ul className="space-y-5">
          {activityTimeline.map((item) => {
            const Icon = iconFor(item.kind);
            return (
              <li key={item.id} className="relative pl-12" data-testid={`activity-${item.id}`}>
                <span
                  className={`absolute left-0 top-0 w-10 h-10 rounded-xl flex items-center justify-center border ${tintFor(
                    item.kind
                  )}`}
                >
                  <Icon size={15} />
                </span>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-[13px] font-bold text-slate-900">{item.title}</p>
                    <p className="text-[12px] text-slate-600 mt-1 leading-relaxed">{item.detail}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10.5px] uppercase tracking-[0.16em] text-slate-400 font-semibold">
                      {item.at}
                    </p>
                    <p className="text-[11.5px] text-slate-700 font-medium mt-0.5">{item.actor}</p>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

// ───────────────────────────────────────────────
// Page
// ───────────────────────────────────────────────
const sectionIds = [
  "overview",
  "team-chat",
  "brochure",
  "documents",
  "viewings",
  "calendar",
  "feedback",
  "readiness",
  "activity",
  "ai",
];

export default function SellerLaunchHubPage() {
  const active = useScrollSpy(sectionIds, 180);
  const handleJump = (id) => {
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({ top: el.offsetTop - 140, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--cf-app-bg)" }}>
      <LaunchHubSidebar activeSection={active} onJump={handleJump} />

      <main
        className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out flex flex-col min-h-screen"
        data-testid="seller-launch-hub-main"
      >
        {/* Page header strip */}
        <div className="sticky top-0 z-30 px-4 md:px-10 py-4 backdrop-blur-xl border-b border-slate-200/60 bg-white/75">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center cf-shadow-md">
                <Rocket size={16} className="text-white" />
              </div>
              <div>
                <p className="cf-eyebrow cf-eyebrow-emerald">Workspace</p>
                <h1 className="cf-h-section text-[18px] mt-0.5">Seller Launch Hub</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button className="btn btn-ghost" data-testid="header-share-btn">
                <Share2 size={13} /> Share
              </button>
              <button className="btn btn-secondary" data-testid="header-invite-btn">
                <Users size={13} /> Invite
              </button>
              <button className="btn btn-primary" data-testid="header-action-btn">
                <Plus size={13} /> New action
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-4 md:px-10 py-6 md:py-9 space-y-12">
          {/* Overview — hero + right panel */}
          <section id="overview" data-testid="overview-section" className="cf-fade-up scroll-mt-24">
            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_400px] gap-6">
              <PropertyHero />
              <RightHeroPanel />
            </div>
          </section>

          <Section
            id="team-chat"
            testid="team-chat-section"
            eyebrow="Operational"
            title="Seller & Agent Team Chat"
            action={
              <button className="btn btn-ghost" data-testid="chat-open-pop-btn">
                <ArrowUpRight size={13} /> Open in full
              </button>
            }
          >
            <TeamChat />
          </Section>

          <Section
            id="brochure"
            testid="brochure-section"
            eyebrow="Approval workflow"
            title="Brochure Review"
          >
            <BrochureReview />
          </Section>

          <Section
            id="viewings"
            testid="viewings-section"
            eyebrow="Buyer pipeline"
            title="Viewing Requests"
            action={
              <button className="btn btn-ghost" data-testid="viewings-add-btn">
                <Plus size={13} /> Add viewing
              </button>
            }
          >
            <ViewingRequests />
          </Section>

          <Section
            id="calendar"
            testid="calendar-section"
            eyebrow="Shared visibility"
            title="Shared Calendar"
            action={
              <div className="flex items-center gap-2 text-[11.5px] text-slate-500">
                <Hammer size={12} /> Contractors
                <Camera size={12} /> Photography
                <Rocket size={12} /> Launch
              </div>
            }
          >
            <SharedCalendar />
          </Section>

          <Section
            id="feedback"
            testid="feedback-section"
            eyebrow="Buyer signals"
            title="Latest Viewing Feedback"
          >
            <FeedbackSection />
          </Section>

          <Section
            id="documents"
            testid="documents-section"
            eyebrow="Compliance"
            title="Seller Documents Hub"
            action={
              <button className="btn btn-primary" data-testid="docs-upload-btn">
                <Upload size={13} /> Upload Document
              </button>
            }
          >
            <DocumentsHub />
          </Section>

          <Section
            id="ai"
            testid="ai-section"
            eyebrow="Intelligence"
            title="Market Readiness Insights"
          >
            <MarketReadinessInsights />
          </Section>

          <Section
            id="activity"
            testid="activity-section"
            eyebrow="Audit trail"
            title="Activity Timeline"
          >
            <ActivityTimelineSection />
          </Section>

          {/* Anchor for "Launch Readiness" sidebar item — scrolls to overview readiness */}
          <div id="readiness" className="-mt-12" />
        </div>

        <footer className="px-4 md:px-10 py-8 text-center text-[11.5px] text-slate-400">
          ChainFlow · Seller Launch Hub · Aigburth Office · Pro plan
        </footer>
      </main>
    </div>
  );
}
